/**
 * Date helpers for the Planner's clamped `datetime-local` inputs.
 *
 * `<input type="datetime-local">` speaks "YYYY-MM-DDTHH:mm" in local time with
 * no zone. The backend stores real ISO 8601 timestamps. These helpers convert
 * between the two and expose a project-range `min`/`max` so an event can never
 * be scheduled outside its parent project window.
 */

/** ISO timestamp → "YYYY-MM-DDTHH:mm" in local time (for an input value). */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "YYYY-MM-DDTHH:mm" (local) → ISO timestamp, or null when empty/invalid. */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Date-only helpers for the project range. Projects are scheduled by calendar
 * day (no time), but the backend stores real timestamps and validates child
 * events against them (`s < group.start` / `e > group.end`). To keep an event
 * that runs in the afternoon of the last day from being rejected, the project
 * START is anchored at the very start of the day and the project END at the
 * very end of the day.
 */

/** ISO timestamp → "YYYY-MM-DD" in local time (for a date-only input value). */
export function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "YYYY-MM-DD" → ISO at the START of that local day (00:00:00.000), or null. */
export function dateInputToIsoStart(value: string | null | undefined): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * "YYYY-MM-DD" → ISO at the END of that local day (23:59:59), or null.
 *
 * NOTE: seconds are whole (no milliseconds) on purpose. MySQL `DATETIME(0)`
 * columns *round* fractional seconds, so `23:59:59.999` would roll over to
 * `00:00:00` of the NEXT day — pushing the stored end one day forward and
 * making the range read "off by one". Whole seconds store verbatim, and still
 * cover every minute-precision event on that day.
 */
export function dateInputToIsoEnd(value: string | null | undefined): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 23, 59, 59, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Min/max bounds for an event date picker, derived from the project range. */
export function rangeBounds(
  projectStart: string | null | undefined,
  projectEnd: string | null | undefined,
): { min?: string; max?: string } {
  return {
    min: projectStart ? isoToLocalInput(projectStart) : undefined,
    max: projectEnd ? isoToLocalInput(projectEnd) : undefined,
  };
}

/** True when [start, end] sits inside the project range (when the range is set). */
export function withinProjectRange(
  start: string | null | undefined,
  end: string | null | undefined,
  projectStart: string | null | undefined,
  projectEnd: string | null | undefined,
): boolean {
  const t = (v: string | null | undefined) => (v ? new Date(v).getTime() : null);
  const s = t(start);
  const e = t(end);
  const gs = t(projectStart);
  const ge = t(projectEnd);
  if (gs != null && s != null && s < gs) return false;
  if (ge != null && e != null && e > ge) return false;
  return true;
}
